// Brings a drill-through target into view inside a Case Sheet tab panel —
// the second half of a FocusRequest (caseSheetTabs.ts says WHERE; this does
// the scrolling and focusing once that tab has actually rendered).
//
// A freshly opened tab mounts its section, which then fetches; the record
// being pointed at does not exist in the DOM until that lands. So this waits
// for the panel to stop showing a loading state (LoadingState carries
// aria-busy) and only then picks the best match — waiting for the exact
// record alone would stall for the full timeout every time the record isn't
// on this page (an amended row, a filtered vitals window).

const FLASH_CLASS = 'cs-focus-flash';
const MAX_WAIT_MS = 4000;
const POLL_MS = 100;

const FOCUSABLE = 'button, input, select, textarea, a[href], [tabindex]';

function findTarget(panel: HTMLElement, targets: string[]): HTMLElement | null {
  for (const t of targets) {
    const el = panel.querySelector<HTMLElement>(`[data-focus~="${CSS.escape(t)}"]`);
    if (el && el.getClientRects().length) return el;
  }
  return null;
}

function stickyOffset(root: HTMLElement, stickyOffsetPx: number): number {
  const header = root.querySelector<HTMLElement>('[data-cs-header]');
  const headerSticky = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
  return stickyOffsetPx + headerSticky + 12;
}

function reveal(el: HTMLElement, offset: number) {
  el.style.scrollMarginTop = `${offset}px`;
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' });
  // A section or row isn't focusable by itself; make it a programmatic focus
  // stop (not a Tab stop) so screen readers announce where the user landed.
  if (!el.matches(FOCUSABLE)) el.setAttribute('tabindex', '-1');
  el.focus({ preventScroll: true });
  el.classList.remove(FLASH_CLASS);
  void el.offsetWidth; // restart the animation on a repeat click
  el.classList.add(FLASH_CLASS);
  window.setTimeout(() => el.classList.remove(FLASH_CLASS), 2200);
}

/** Returns a cancel function, for an effect cleanup. */
export function focusInPanel(
  root: HTMLElement, tab: string, targets: string[], stickyOffsetPx: number,
): () => void {
  const started = Date.now();
  let timer = 0;
  let cancelled = false;

  const attempt = () => {
    if (cancelled) return;
    const panel = root.querySelector<HTMLElement>(`[data-tab-panel="${CSS.escape(tab)}"]`);
    const busy = !panel || !!panel.querySelector('[aria-busy="true"]');
    const el = panel ? findTarget(panel, targets) : null;
    const timedOut = Date.now() - started > MAX_WAIT_MS;
    if (!el && busy && !timedOut) {
      timer = window.setTimeout(attempt, POLL_MS);
      return;
    }
    // Nothing more specific exists — land on the panel's first section so the
    // jump is still visible, rather than leaving the user where they were.
    const fallback = panel?.querySelector<HTMLElement>('section') ?? panel;
    const target = el ?? fallback;
    if (target) reveal(target, stickyOffset(root, stickyOffsetPx));
  };

  // Let the tab switch paint first.
  timer = window.setTimeout(attempt, 0);
  return () => { cancelled = true; window.clearTimeout(timer); };
}
